import { useState, useRef } from 'react'
import { PackageWorkflowServiceModel } from 'app/modules/service-management/models'
import { MAX_SIZE_SERVICE_ICON } from 'app/utils/common/constants'
import { t } from 'i18next'

interface UseServiceFormProps {
  setFieldValue: (field: string, value: any) => void
}

export const useServiceForm = ({ setFieldValue }: UseServiceFormProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileError, setFileError] = useState<string>("")
  const [iconPreview, setIconPreview] = useState<string>("")

  // Convert form values to FormData for Add Service
  const convertAddDataSubmit = (value: PackageWorkflowServiceModel & {
    startWorkingTime?: number
    endWorkingTime?: number
    visitDuration?: number
    visitWithBufferTime?: number
    earliestBookingNoticeOffset?: number
    bufferTimeBetweenBookings?: number
    distributeWorkerMinuteOffset?: number
  }) => {
    const formData = new FormData()
    formData.append('nameAr', value?.nameAr || '')
    formData.append('nameEn', value?.nameEn || '')
    formData.append('descriptionAr', value?.subTitleTextAr || '')
    formData.append('descriptionEn', value?.subTitleTextEn || '')
    formData.append('status', String(value?.status ? true : false))
    
    // Icon is required in backend, append it if exists (File object)
    if (value?.icon && value.icon instanceof File) {
      formData.append('icon', value.icon)
    }
    
    // WorkerDistributeOffset (optional)
    if (value?.distributeWorkerMinuteOffset !== undefined && value?.distributeWorkerMinuteOffset !== null) {
      formData.append('workerDistributeOffset', String(value.distributeWorkerMinuteOffset))
    }
    
    // WorkingHours object - using dot notation for nested properties
    formData.append('workingHours.StartWorkingHour', String(value?.startWorkingTime || 0))
    formData.append('workingHours.EndWorkingHour', String(value?.endWorkingTime || 0))
    
    // TimeConfiguration object - using dot notation for nested properties
    // Default values: Visit Duration: 60, Visit with Buffer Time: 30, Earliest Booking Notice: 60, Buffer Time Between Bookings: 30
    formData.append('timeConfiguration.DurationVisitTime', String(value?.visitDuration ?? 60))
    formData.append('timeConfiguration.BufferVisitTime', String(value?.visitWithBufferTime ?? 30))
    formData.append('timeConfiguration.CurrentTimeOffset', String(value?.earliestBookingNoticeOffset ?? 60))
    formData.append('timeConfiguration.BlockWorkingHour', String(value?.bufferTimeBetweenBookings ?? 30))

    // Rescheduling Configuration for PWA 
    // Default values: Reschedule After Hours: 168 (7 days), Reschedule Before Minutes: 720 (12 hours), Enable Rescheduling: false, Charge Fee Within Cutoff: 0, Charge Fee Within Cutoff With VAT: 0
    formData.append('reschedulingConfig.rescheduleFeeAmount', String(value?.rescheduleFeeAmount || 0))
    formData.append('reschedulingConfig.rescheduleFeeAmountWithVat', String(value?.rescheduleFeeAmountWithVat || 0))
    formData.append('reschedulingConfig.rescheduleWindowDays', String((value?.rescheduleWindowDays ?? 7)))
    formData.append('reschedulingConfig.rescheduleBeforeMinutes', String(value?.rescheduleBeforeMinutes|| 720))
    formData.append('reschedulingConfig.enableRescheduling', String(value?.enableRescheduling ? true : false))

    // AutoServiceComplete
    // Default values: DeliveredOrdersMinutes: 15, UndeliveredOrdersMinutes: 10
    formData.append('autoServiceConfiguration.DeliveredOrdersVisitEnd', String(value?.deliveredOrdersVisitEnd ? true : false))
    formData.append('autoServiceConfiguration.DeliveredOrdersMinutes', String(value?.deliveredOrdersMinutes || 15))
    formData.append('autoServiceConfiguration.PickedUpFeature', String(value?.pickedUpFeature ? true : false))
    formData.append('autoServiceConfiguration.UndeliveredOrdersMinutes', String(value?.undeliveredOrdersMinutes || 10))
    
    // National ID Configuration
    formData.append('isShowNationalId', String(value?.isShowNationalId ? true : false))
    
    return formData
  }

  // Convert form values to FormData for Edit Service
  const convertEditDataSubmit = (value: PackageWorkflowServiceModel & {
    startWorkingTime?: number
    endWorkingTime?: number
    visitDuration?: number
    visitWithBufferTime?: number
    earliestBookingNoticeOffset?: number
    bufferTimeBetweenBookings?: number
  }) => {
    const formData = new FormData()
    formData.append('id', String(value?.id || 0))
    formData.append('nameAr', value?.nameAr || '')
    formData.append('nameEn', value?.nameEn || '')
    formData.append('descriptionAr', value?.subTitleTextAr || '')
    formData.append('descriptionEn', value?.subTitleTextEn || '')
    formData.append('status', String(value?.status ? true : false))
    formData.append('published', String(value?.status ? value?.published : false))
    formData.append('packageFlowId', String(value?.packageWorkFlowId || 0))
    formData.append('majorId', String(value?.id || 0)) // majorId is the id of the service
    
    // Handle icon based on type
    if (value?.icon && value.icon instanceof File) {
      // Case 1: New icon uploaded (File) → IsUpdateIcon = true
      formData.append('icon', value.icon)
      formData.append('isUpdateIcon', 'true')
    } else if (value?.icon && typeof value.icon === 'string' && value.icon.length > 0) {
      // Case 2: Keep existing icon (string URL) → IsUpdateIcon = false
      // API requires Icon field even when not updating, so send empty file
      const emptyBlob = new Blob([''], { type: 'image/png' })
      const emptyFile = new File([emptyBlob], '', { type: 'image/png' })
      formData.append('icon', emptyFile)
      formData.append('isUpdateIcon', 'false')
    } else {
      // Case 3: No icon → IsUpdateIcon = null (validation should prevent this)
      // Don't append anything, API will handle as null
    }
    
    // WorkingHours object - using dot notation for nested properties
    formData.append('workingHours.StartWorkingHour', String(value?.startWorkingTime || 0))
    formData.append('workingHours.EndWorkingHour', String(value?.endWorkingTime || 0))
    
    // TimeConfiguration object - using dot notation for nested properties
    // Default values: Visit Duration: 60, Visit with Buffer Time: 30, Earliest Booking Notice: 60, Buffer Time Between Bookings: 30
    formData.append('timeConfiguration.DurationVisitTime', String(value?.visitDuration ?? 60))
    formData.append('timeConfiguration.BufferVisitTime', String(value?.visitWithBufferTime ?? 30))
    formData.append('timeConfiguration.CurrentTimeOffset', String(value?.earliestBookingNoticeOffset ?? 60))
    formData.append('timeConfiguration.BlockWorkingHour', String(value?.bufferTimeBetweenBookings ?? 30))

    // Rescheduling Configuration for PWA 
    // Default values: Reschedule After Hours: 168 (7 days), Reschedule Before Minutes: 720 (12 hours), Enable Rescheduling: false, Charge Fee Within Cutoff: 0, Charge Fee Within Cutoff With VAT: 0
    formData.append('reschedulingConfig.rescheduleFeeAmount', String(value?.rescheduleFeeAmount || 0))
    formData.append('reschedulingConfig.rescheduleFeeAmountWithVat', String(value?.rescheduleFeeAmountWithVat || 0))
    formData.append('reschedulingConfig.rescheduleWindowDays', String((value?.rescheduleWindowDays ?? 7)))
    formData.append('reschedulingConfig.rescheduleBeforeMinutes', String(value?.rescheduleBeforeMinutes|| 720))
    formData.append('reschedulingConfig.enableRescheduling', String(value?.enableRescheduling ? true : false))
    
    // AutoServiceComplete
    // Default values: DeliveredOrdersMinutes: 15, UndeliveredOrdersMinutes: 10
    formData.append('autoServiceConfiguration.DeliveredOrdersVisitEnd', String(value?.deliveredOrdersVisitEnd ? true : false))
    formData.append('autoServiceConfiguration.DeliveredOrdersMinutes', String(value?.deliveredOrdersMinutes || 15))
    formData.append('autoServiceConfiguration.PickedUpFeature', String(value?.pickedUpFeature ? true : false))
    formData.append('autoServiceConfiguration.UndeliveredOrdersMinutes', String(value?.undeliveredOrdersMinutes || 10))
    
    // National ID Configuration
    formData.append('isShowNationalId', String(value?.isShowNationalId ? true : false))
    
    return formData
  }

  const updateSameDayBooking = (value: PackageWorkflowServiceModel) => {
    const data = {
      id: value?.packageWorkFlowId || 0,
      sameDayBookingNoteAr: value?.sameDayBookingNoteAr || '',
      sameDayBookingNoteEn: value?.sameDayBookingNoteEn || '',
      isActiveSamedayBooking: value?.isActiveSamedayBooking ? true : false,
      distributeWorkerMinuteOffset: value?.distributeWorkerMinuteOffset || 0,
      currentTimeOffset: value?.earliestBookingNoticeOffset || 0
    }
    
    return data
  }
  // Handle file selection and validation
  const handleFileChange = (e: any) => {
    const file = e.target.files[0]
    if (!file) {
      // User cancelled file selection - keep existing preview/icon
      return
    }
    
    let error = {
      format: false,
      size: false,
      ratio: false
    }
    
    // Check 1: file format (PNG)
    const isValidFormat = file.type === 'image/png'
    if (!isValidFormat) {
      error.format = true
    }
    
    // Check 2: file size (max 500 KB)
    const isValidSize = file.size <= MAX_SIZE_SERVICE_ICON
    if (!isValidSize) {
      error.size = true
    }
    
    // Check aspect ratio (1:1)
    const reader = new FileReader()
    reader.onload = (event: any) => {
      const img = new Image()
      img.onload = () => {
        const isValidRatio = img.width === img.height
        if (!isValidRatio) {
          error.ratio = true
        }
        
        // Check all validation results
        if (!error.format && !error.size && !error.ratio) {
          // All validations passed (3/3 correct, 0/3 wrong)
          setFileError("")
          setIconPreview(event.target.result)
          setFieldValue("icon", file)
        } else if (error.format && !error.size && !error.ratio) {
          // Only format error (2/3 correct, 1/3 wrong)
          setFileError(t("SERVICE_MANAGEMENT_PAGE.SERVICE.ONLY_PNG_ALLOWED"))
          handleRemoveIconError()
        } else if (error.size && !error.format && !error.ratio) {
          // Only size error (2/3 correct, 1/3 wrong)
          setFileError(t("SERVICE_MANAGEMENT_PAGE.SERVICE.FILE_SIZE_EXCEED"))
          handleRemoveIconError()
        } else if (error.ratio && !error.format && !error.size) {
          // Only ratio error (2/3 correct, 1/3 wrong)
          setFileError(t("SERVICE_MANAGEMENT_PAGE.SERVICE.INVALID_ASPECT_RATIO"))
          handleRemoveIconError()
        } else if (
          (!error.format && error.size && error.ratio) ||
          (error.format && !error.size && error.ratio) ||
          (error.format && error.size && !error.ratio)
        ) {
          // Two errors (1/3 correct, 2/3 wrong)
          setFileError(t("SERVICE_MANAGEMENT_PAGE.SERVICE.HAS_2_DIFFERENT_FORMAT"))
          handleRemoveIconError()
        } else {
          // All three errors (0/3 correct, 3/3 wrong)
          setFileError(t("SERVICE_MANAGEMENT_PAGE.SERVICE.HAS_3_DIFFERENT_FORMAT"))
          handleRemoveIconError()
        }
      }
      img.src = event.target.result
    }
    reader.readAsDataURL(file)
  }

  // Remove icon completely
  const removeIcon = () => {
    setIconPreview("")
    setFieldValue("icon", "")
    setFileError("")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Remove icon on error (keep error message)
  const handleRemoveIconError = () => {
    setIconPreview("")
    setFieldValue("icon", "")
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Click handler for SVG icon
  const handleSVGClick = () => {
    fileInputRef.current?.click()
  }

  return {
    fileInputRef,
    fileError,
    setFileError,
    iconPreview,
    setIconPreview,
    convertAddDataSubmit,
    convertEditDataSubmit,
    updateSameDayBooking,
    handleFileChange,
    removeIcon,
    handleRemoveIconError,
    handleSVGClick
  }
}

